<script setup>
import {
  defineAsyncComponent,
  nextTick,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
} from "vue";
import { activeWorkspace } from "./lib/route-workspace";
import { useRoute } from "vue-router";
import { getCurrentUser } from "./lib/current-user";
import { apiFetch } from "./lib/api";
import { getSeoForPath } from "./lib/seo";
import SessionExpiredDialog from "./components/SessionExpiredDialog.vue";

const route = useRoute();
const TosAcceptModal = defineAsyncComponent(
  () => import("./components/TosAcceptModal.vue"),
);
const UpgradeBanner = defineAsyncComponent(
  () => import("./components/UpgradeBanner.vue"),
);

const showBanner = ref(false);
const bannerChecked = ref(false);
const routeContent = ref(null);
const routeAnnouncement = ref("");

function mainContentTarget() {
  const target = routeContent.value.querySelector("main") || routeContent.value;
  if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
  return target;
}

function focusMainContent(options) {
  mainContentTarget().focus(options);
}

async function checkBanner() {
  try {
    const user = await getCurrentUser();
    if (!user || user.isAdmin || user.isBeta) {
      bannerChecked.value = true;
      return;
    }
    const s = user.subscription;
    const isPro =
      s &&
      ["pro", "planner", "complete", "texas"].includes(s.plan) &&
      ["active", "trialing"].includes(s.status);
    if (isPro) {
      showBanner.value = false;
      bannerChecked.value = true;
      return;
    }
    const res = await apiFetch("/api/routes/usage");
    if (!res.ok) return;
    const usage = await res.json();
    showBanner.value = usage.usedTotal >= 1;
    bannerChecked.value = true;
  } catch {
    // non-fatal
  }
}

// Re-check on route navigation so the banner appears after first route
watch(
  () => route.path,
  () => {
    if (route.meta.requiresAuth) {
      bannerChecked.value = false;
      checkBanner();
    }
  },
  { immediate: true },
);

watch(
  () => route.path,
  async (path) => {
    await nextTick();
    focusMainContent({ preventScroll: true });
    routeAnnouncement.value = `${getSeoForPath(path).title} loaded`;
  },
);

function onRouteComplete() {
  bannerChecked.value = false;
  checkBanner();
}
onMounted(() => window.addEventListener("pp-route-complete", onRouteComplete));
onBeforeUnmount(() =>
  window.removeEventListener("pp-route-complete", onRouteComplete),
);
</script>

<template>
  <div>
    <a
      class="skip-link"
      href="#main-content"
      @click.prevent="focusMainContent()"
    >
      Skip to main content
    </a>
    <p class="sr-only" role="status" aria-live="polite" aria-atomic="true">
      {{ routeAnnouncement }}
    </p>
    <UpgradeBanner v-if="route.meta.requiresAuth" :show="showBanner" />
    <div id="main-content" ref="routeContent" tabindex="-1">
      <RouterView :key="activeWorkspace" />
    </div>
    <TosAcceptModal v-if="route.meta.requiresAuth || route.path === '/s'" />
    <SessionExpiredDialog v-if="route.meta.requiresAuth" />
  </div>
</template>
